/**
 * useCurrencyAccounts Hook
 * 
 * Fetches accounts data to check currency usage.
 */

import { useEffect, useState } from "react";
import type React from "react";
import { auth, db } from "../../../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  QuerySnapshot,
  DocumentSnapshot,
  Unsubscribe,
  FirestoreError,
} from "firebase/firestore";
import { Account } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";

export interface UseCurrencyAccountsParams {
  workspaceId: string;
}

export interface UseCurrencyAccountsResult {
  accounts: Account[];
  setAccounts: React.Dispatch<React.SetStateAction<Account[]>>;
}

/**
 * Hook for fetching accounts data
 */
export const useCurrencyAccounts = ({ workspaceId }: UseCurrencyAccountsParams): UseCurrencyAccountsResult => {
  const [accounts, setAccounts] = useState<Account[]>([]);

  useEffect(() => {
    if (!workspaceId || (!auth.currentUser && !isDemoMode())) return;

    const accountsRef = collection(db, "workspaces", workspaceId, "accounts");
    const q = query(accountsRef, orderBy("accountName", "asc"));

    const handleSnapshot = (snapshot: QuerySnapshot): void => {
      const accountsList: Account[] = snapshot.docs.map((docSnap: DocumentSnapshot) => {
        const data = docSnap.data();
        if (!data) return null;
        return {
          id: docSnap.id,
          userId: data.userId as string,
          accountName: data.accountName as string,
          group: data.group as import("../../../types").AccountGroup,
          amount: data.amount as number,
          currency: data.currency as string,
          description: data.description as string,
          createdAt: data.createdAt,
        };
      }).filter((acc): acc is Account => acc !== null);
      
      setAccounts(accountsList);
    };

    let unsubscribe: Unsubscribe | undefined;

    try {
      unsubscribe = onSnapshot(
        q,
        handleSnapshot,
        (error: FirestoreError) => {
          console.error("Error fetching accounts:", error);
          // Fallback query without orderBy
          if (error.code === "failed-precondition" || error.message?.includes("index")) {
            const fallbackQ = query(accountsRef);
            unsubscribe = onSnapshot(
              fallbackQ,
              handleSnapshot,
              (fallbackError: FirestoreError) => {
                console.error("Fallback query also failed:", fallbackError);
                setAccounts([]);
              }
            );
          } else {
            setAccounts([]);
          }
        }
      );
    } catch (error) {
      console.error("Query setup error:", error);
      setAccounts([]);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [workspaceId]);

  return {
    accounts,
    setAccounts,
  };
};
